/**
 * Persist Messages compose draft in localStorage (per user).
 * Survives tab switches and browser restarts; cleared when empty.
 */

export interface MessagesComposeDraft {
  subject: string;
  content: string;
  leadId: string | null;
  projectId: string | null;
  /** Project file ids selected as Gmail attachments */
  attachmentIds: string[];
  activeTemplateId: string | null;
  updatedAt: number;
}

function storageKey(userId: string): string {
  return `leadsme:messages:composeDraft:${userId}`;
}

function normalizeAttachmentIds(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

function isEmptyDraft(draft: MessagesComposeDraft): boolean {
  return (
    !draft.subject.trim() &&
    !draft.content.trim() &&
    !draft.leadId &&
    !draft.projectId &&
    draft.attachmentIds.length === 0 &&
    !draft.activeTemplateId
  );
}

export function loadMessagesComposeDraft(userId: string): MessagesComposeDraft | null {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<MessagesComposeDraft>;
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      subject: typeof parsed.subject === 'string' ? parsed.subject : '',
      content: typeof parsed.content === 'string' ? parsed.content : '',
      leadId: typeof parsed.leadId === 'string' ? parsed.leadId : null,
      projectId: typeof parsed.projectId === 'string' ? parsed.projectId : null,
      attachmentIds: normalizeAttachmentIds(parsed.attachmentIds),
      activeTemplateId:
        typeof parsed.activeTemplateId === 'string' ? parsed.activeTemplateId : null,
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

export function saveMessagesComposeDraft(userId: string, draft: MessagesComposeDraft): void {
  try {
    if (isEmptyDraft(draft)) {
      localStorage.removeItem(storageKey(userId));
      return;
    }
    localStorage.setItem(storageKey(userId), JSON.stringify(draft));
  } catch {
    // ignore quota / private mode
  }
}

export function clearMessagesComposeDraft(userId: string): void {
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}
